import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import apiClient from '../api/client';
import './DashboardPage.css';

interface DashboardStats {
  totalEmployees: number;
  activeEmployees: number;
  employeesOnLeave: number;
  suspendedOrTerminated: number;
  pendingReceipts: number;
  nonConformingSignatures: number;
  pendingLeaves: number;
  currentlyAbsent: number;
  upcomingReturns: number;
  pendingCertificates: number;
  documentsExpiring: number;
  expiredDocuments: number;
  pendingRequiredDocumentation: number;
  details: Record<string, DashboardDetail[]>;
}
interface DashboardDetail {
  employeeId?: string;
  employee: string;
  status: string;
  subtitle?: string;
  period?: string;
  link?: string;
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [stats, setStats] = useState<DashboardStats>({} as DashboardStats);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }

    apiClient
      .get('/reports/dashboard')
      .then((response) => setStats(response.data))
      .finally(() => setIsLoading(false));
  }, [user, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };
  const metrics: Array<[keyof DashboardStats, string, string, string]> = [
    ['totalEmployees', 'Total de empleados', '👥', 'blue'],
    ['activeEmployees', 'Empleados activos', '✓', 'green'],
    ['employeesOnLeave', 'Empleados de licencia', '🏥', 'violet'],
    ['suspendedOrTerminated', 'Suspendidos o de baja', '⏸', 'gray'],
    ['pendingReceipts', 'Recibos pendientes', '📄', 'amber'],
    ['nonConformingSignatures', 'Firmas en disconformidad', '!', 'red'],
    ['pendingLeaves', 'Licencias pendientes', '⏳', 'amber'],
    ['currentlyAbsent', 'Ausentes actualmente', '📅', 'violet'],
    ['upcomingReturns', 'Próximas reincorporaciones', '↩', 'blue'],
    ['pendingCertificates', 'Certificados pendientes', '🩺', 'amber'],
    ['documentsExpiring', 'Documentos por vencer', '⌛', 'amber'],
    ['expiredDocuments', 'Documentos vencidos', '!', 'red'],
    ['pendingRequiredDocumentation', 'Documentación pendiente', '📁', 'red'],
  ];
  const selected = metrics.find(([key]) => key === selectedMetric);
  const details = selectedMetric ? stats.details?.[selectedMetric] || [] : [];

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-left">
          <h1>Recibos Digitales</h1>
        </div>
        <div className="header-right">
          <span className="user-name">
            {user?.firstName} {user?.lastName}
          </span>
          <button onClick={handleLogout} className="logout-button">
            Cerrar Sesión
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="welcome-section">
          <h2>Bienvenido, {user?.firstName}!</h2>
          <p>Panel de administración de recibos digitales</p>
        </div>

        {isLoading ? (
          <div className="loading">Cargando...</div>
        ) : (
          <>
            <div className="stats-grid">
              {metrics.map(([key, label, icon, tone]) => (
                <button
                  className={`stat-card tone-${tone}`}
                  key={key as string}
                  onClick={() => setSelectedMetric(key as string)}
                >
                  <span className="stat-icon" aria-hidden="true">
                    {icon}
                  </span>
                  <div className="stat-value">{(stats[key] as number) ?? 0}</div>
                  <div className="stat-label">{label}</div>
                  <span className="stat-open">Ver detalle →</span>
                </button>
              ))}
            </div>

            <div className="action-grid">
              {user?.roles?.includes('SUPERADMIN') && (
                <div className="action-card" onClick={() => handleNavigate('/admin/companies')}>
                  <div className="action-icon">🏢</div>
                  <div className="action-title">Empresas</div>
                  <div className="action-description">Altas, cupos e importación masiva</div>
                </div>
              )}
              <div className="action-card" onClick={() => handleNavigate('/employees')}>
                <div className="action-icon">👥</div>
                <div className="action-title">Empleados</div>
                <div className="action-description">Gestionar empleados</div>
              </div>
              <div className="action-card" onClick={() => handleNavigate('/receipts')}>
                <div className="action-icon">📄</div>
                <div className="action-title">Recibos</div>
                <div className="action-description">Subir y gestionar recibos</div>
              </div>
              <div className="action-card" onClick={() => handleNavigate('/leaves')}>
                <div className="action-icon">📅</div>
                <div className="action-title">Licencias</div>
                <div className="action-description">Vacaciones y ausencias</div>
              </div>
              <div className="action-card" onClick={() => handleNavigate('/medical-certificates')}>
                <div className="action-icon">🩺</div>
                <div className="action-title">Certificados médicos</div>
                <div className="action-description">Bandeja de revisión protegida</div>
              </div>
              <div className="action-card" onClick={() => handleNavigate('/audit')}>
                <div className="action-icon">📋</div>
                <div className="action-title">Auditoría</div>
                <div className="action-description">Ver registros de auditoría</div>
              </div>
              <div className="action-card" onClick={() => handleNavigate('/reports')}>
                <div className="action-icon">📊</div>
                <div className="action-title">Reportes de RR. HH.</div>
                <div className="action-description">Filtros, Excel, PDF y análisis integral</div>
              </div>
            </div>
          </>
        )}
      </div>
      {selectedMetric && (
        <div
          className="metric-overlay"
          role="presentation"
          onMouseDown={() => setSelectedMetric(null)}
        >
          <section
            className="metric-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="metric-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <header>
              <div>
                <span className="eyebrow">Detalle del indicador</span>
                <h2 id="metric-title">{selected?.[1]}</h2>
                <p>
                  {details.length} {details.length === 1 ? 'resultado' : 'resultados'} en la empresa
                </p>
              </div>
              <button aria-label="Cerrar" onClick={() => setSelectedMetric(null)}>
                ×
              </button>
            </header>
            <div className="metric-list">
              {!details.length ? (
                <div className="metric-empty">
                  <span>✓</span>
                  <strong>No hay casos para revisar</strong>
                  <small>Este indicador no tiene resultados actualmente.</small>
                </div>
              ) : (
                details.map((item, index) => (
                  <article className="metric-item" key={`${item.employeeId}-${index}`}>
                    <div className="metric-avatar">
                      {item.employee
                        .split(' ')
                        .slice(0, 2)
                        .map((part) => part[0])
                        .join('')}
                    </div>
                    <div>
                      <strong>{item.employee}</strong>
                      {item.subtitle && <span>{item.subtitle}</span>}
                      {item.period && (
                        <small>
                          {item.period.includes('|')
                            ? item.period
                                .split('|')
                                .map((date) => new Date(date).toLocaleDateString('es-AR'))
                                .join(' al ')
                            : `Fecha: ${new Date(item.period).toLocaleDateString('es-AR')}`}
                        </small>
                      )}
                    </div>
                    {item.link && (
                      <button className="secondary-button" onClick={() => navigate(item.link!)}>
                        Abrir
                      </button>
                    )}
                  </article>
                ))
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
